import { Coordinates } from '../Mapbox'
import { useTranslation } from '../Translation'
import { PrefectureIndex } from '../Util'
import { useJapaneseArtisanArticles } from './useJapaneseArtisanArticles'
import { useEnglishArtisanArticles } from './useEnglishArtisanArticles'

export type Artisan = ArtisanPartFromMicrocms & ArtisanPartFromMapbox

type Prefecture = keyof typeof PrefectureIndex
export type ArtisanPartFromMicrocms = {
  id: string
  workshop: string
  workshopEn: string
  prefectureIndex: number
  prefecture: Prefecture
  city: string
  blogIds: string[]
}

export type ArtisanPartFromMapbox = {
  coordinates: Coordinates
  prefectureEn: string
  cityEn: string
}

export type RawArtisanPartFromMicrocms = {
  id: string
  workshop: string
  workshopEn: string
  location: string
  blogIds: { id: string }[]
}

export const convertToArtisanPartFromMicrocms = (
  rawData: RawArtisanPartFromMicrocms,
): ArtisanPartFromMicrocms => {
  // 既に半角 or 全角スペースが入っていても対応できるように設定
  const regex = /(.{2,3}[都道府県])[\u{20}\u{3000}]?/u
  const prefecture = rawData.location.match(regex)?.[1] as
    | Prefecture
    | undefined
  const city = rawData.location.replace(regex, '')
  const prefectureIndex =
    PrefectureIndex[prefecture as keyof typeof PrefectureIndex]

  if (prefecture === undefined || !prefectureIndex) {
    throw new Error(`都道府県名が不正です: ${prefecture}`)
  }
  return {
    ...rawData,
    prefectureIndex,
    prefecture,
    city,
    blogIds: rawData.blogIds.map(blogId => blogId.id),
  }
}

export const useArtisanArticles = () => {
  const { locale } = useTranslation()
  const japanesseArticles = useJapaneseArtisanArticles()
  const englishArticles = useEnglishArtisanArticles()

  return locale === 'en' ? englishArticles : japanesseArticles
}

type ArtisanFeature = {
  id: string
  name: string
  nameEn: string
  message: string
  title: string
  titleEn: string
  profile: string
  articleIds: string[]
}
export const getArtisanFeatures = (): ArtisanFeature[] => [
  {
    id: '001',
    name: '中川 衛',
    nameEn: 'Mamoru Nakagawa',
    message: '電機メーカーの社員から、<br />伝統工芸の頂点へ。',
    title: '人間国宝・重要無形文化財「彫金」保持者',
    titleEn:
      'Living National Treasure<br />Important Intangible Cultural Property “Metal Engraving” Preserver',
    profile: '/assets/artisan/feature001.jpg',
    articleIds: [
      'mamoru_nakagawa1',
      'mamoru_nakagawa2',
      'mamoru_nakagawa3',
      'mamoru_nakagawa4',
      'mamoru_nakagawa5',
    ],
  },
]

export type ArtisanSeries = {
  seriesId: string
  title: string
  titleEn: string
  subTitle: string
  subTitleEn: string
  description: string
  descriptionEn: string
  articleIds: string[]
}
type RawArtisanSeries = {
  id: string
  title: string
  titleEn: string
  subTitle: string
  subTitleEn: string
  description: string
  descriptionEn: string
  articles: { id: string; publishDate: string }[]
}
export const convertToArtisanSeries = (
  rawSeries: RawArtisanSeries,
): ArtisanSeries => ({
  seriesId: rawSeries.id,
  title: rawSeries.title,
  titleEn: rawSeries.titleEn,
  subTitle: rawSeries.subTitle,
  subTitleEn: rawSeries.subTitleEn,
  description: rawSeries.description,
  descriptionEn: rawSeries.descriptionEn,
  articleIds: rawSeries.articles
    .sort(
      (a, b) =>
        new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime(),
    )
    .map(article => article.id),
})

export type ArtisanDictionary = {
  ja: string
  en: string
  articleIds?: string[]
  imageUrl?: string
}
type RawArtisanDictionary = {
  tag: string
  tagEn: string
  article?: { id: string; publishDate: string }
  image?: { url: string }
}
export const convertToArtisanDictionary = (
  rawArtisanDictionary: RawArtisanDictionary,
): ArtisanDictionary => ({
  ja: rawArtisanDictionary.tag,
  en: rawArtisanDictionary.tagEn,
  imageUrl: rawArtisanDictionary.image?.url,
  articleIds: rawArtisanDictionary.article
    ? [rawArtisanDictionary.article.id]
    : undefined,
})

export const buildDictionaryLink = (tag: string) =>
  `/artisan/dictionary?q=${tag}`
